import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/Input/Input';
import type { VenueMealsBreakdown } from './types';
import { CURRENCY_INFO, type SupportedCurrency } from './types';

export interface MealBreakdownProps {
  shortlistId: string;
  breakdown: VenueMealsBreakdown;
  attendees: number;
  displayCurrency: string;
  onChange: (data: Partial<VenueMealsBreakdown>) => void;
}

export function MealBreakdown({
  shortlistId,
  breakdown,
  attendees,
  displayCurrency,
  onChange,
}: MealBreakdownProps) {
  const currency = displayCurrency as SupportedCurrency;
  const currencyInfo = CURRENCY_INFO[currency] ?? CURRENCY_INFO.USD;

  // Helper to format currency value from cents to dollars with 2 decimal places
  const formatCurrencyValue = (cents: number | null): string => {
    if (cents === null) return '';
    return (cents / 100).toFixed(2);
  };

  // Track which field is currently being edited
  const [editingField, setEditingField] = useState<string | null>(null);

  // Local state for controlled inputs
  const [localValues, setLocalValues] = useState({
    breakfast_cost_per_attendee: formatCurrencyValue(breakdown.breakfast_cost_per_attendee),
    breakfast_count: breakdown.breakfast_count?.toString() ?? '0',
    lunch_cost_per_attendee: formatCurrencyValue(breakdown.lunch_cost_per_attendee),
    lunch_count: breakdown.lunch_count?.toString() ?? '0',
    dinner_cost_per_attendee: formatCurrencyValue(breakdown.dinner_cost_per_attendee),
    dinner_count: breakdown.dinner_count?.toString() ?? '0',
    alcohol_cost_per_attendee: formatCurrencyValue(breakdown.alcohol_cost_per_attendee),
    alcohol_count: breakdown.alcohol_count?.toString() ?? '0',
  });

  // Sync local values when breakdown prop changes (but not for fields currently being edited)
  useEffect(() => {
    setLocalValues(prev => ({
      breakfast_cost_per_attendee: editingField === 'breakfast_cost_per_attendee' ? prev.breakfast_cost_per_attendee : formatCurrencyValue(breakdown.breakfast_cost_per_attendee),
      breakfast_count: editingField === 'breakfast_count' ? prev.breakfast_count : (breakdown.breakfast_count?.toString() ?? '0'),
      lunch_cost_per_attendee: editingField === 'lunch_cost_per_attendee' ? prev.lunch_cost_per_attendee : formatCurrencyValue(breakdown.lunch_cost_per_attendee),
      lunch_count: editingField === 'lunch_count' ? prev.lunch_count : (breakdown.lunch_count?.toString() ?? '0'),
      dinner_cost_per_attendee: editingField === 'dinner_cost_per_attendee' ? prev.dinner_cost_per_attendee : formatCurrencyValue(breakdown.dinner_cost_per_attendee),
      dinner_count: editingField === 'dinner_count' ? prev.dinner_count : (breakdown.dinner_count?.toString() ?? '0'),
      alcohol_cost_per_attendee: editingField === 'alcohol_cost_per_attendee' ? prev.alcohol_cost_per_attendee : formatCurrencyValue(breakdown.alcohol_cost_per_attendee),
      alcohol_count: editingField === 'alcohol_count' ? prev.alcohol_count : (breakdown.alcohol_count?.toString() ?? '0'),
    }));
  }, [breakdown, editingField]);

  const handleChange = (field: keyof typeof localValues, value: string) => {
    // Preserve user input as-is while typing
    setLocalValues(prev => ({ ...prev, [field]: value }));

    // Convert to DB format and notify parent
    const numValue = parseFloat(value) || 0;
    let dbValue: number | null = null;

    if (field.includes('cost_per_attendee')) {
      // Convert dollars to cents
      dbValue = value ? Math.round(numValue * 100) : null;
    } else {
      // Count fields
      dbValue = Math.round(numValue);
    }

    onChange({ [field]: dbValue });
  };

  // Handle focus to track editing state
  const handleFocus = (field: keyof typeof localValues) => {
    setEditingField(field);
  };

  // Format currency values on blur to ensure 2 decimal places
  const handleBlur = (field: keyof typeof localValues) => {
    setEditingField(null);
    if (field.includes('cost_per_attendee')) {
      const numValue = parseFloat(localValues[field]);
      if (!isNaN(numValue)) {
        setLocalValues(prev => ({ ...prev, [field]: numValue.toFixed(2) }));
      }
    }
  };

  // Calculate totals for display
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const breakfastCost = parseFloat(localValues.breakfast_cost_per_attendee) || 0;
  const breakfastCount = parseInt(localValues.breakfast_count) || 0;
  const breakfastTotal = breakfastCost * breakfastCount * attendees;

  const lunchCost = parseFloat(localValues.lunch_cost_per_attendee) || 0;
  const lunchCount = parseInt(localValues.lunch_count) || 0;
  const lunchTotal = lunchCost * lunchCount * attendees;

  const dinnerCost = parseFloat(localValues.dinner_cost_per_attendee) || 0;
  const dinnerCount = parseInt(localValues.dinner_count) || 0;
  const dinnerTotal = dinnerCost * dinnerCount * attendees;

  const alcoholCost = parseFloat(localValues.alcohol_cost_per_attendee) || 0;
  const alcoholCount = parseInt(localValues.alcohol_count) || 0;
  const alcoholTotal = alcoholCost * alcoholCount * attendees;

  const grandTotal = breakfastTotal + lunchTotal + dinnerTotal + alcoholTotal;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-amber-900">Meal Breakdown</h3>
        <p className="text-sm text-amber-700 mt-1">Costs are per attendee, multiplied by count</p>
      </div>

      <table className="w-full text-sm table-fixed">
        <colgroup>
          <col className="w-auto" />
          <col className="w-40" />
          <col className="w-28" />
          <col className="w-32" />
        </colgroup>
        <thead>
          <tr className="text-left border-b border-amber-200">
            <th className="py-2 px-3 font-medium text-amber-900">Meal Type</th>
            <th className="py-2 px-3 font-medium text-amber-900 text-right">Cost/Attendee ({currencyInfo.symbol})</th>
            <th className="py-2 px-3 font-medium text-amber-900 text-right">Count</th>
            <th className="py-2 px-3 font-medium text-amber-900 text-right w-32">Total ({currencyInfo.symbol})</th>
          </tr>
        </thead>
        <tbody>
          {/* Breakfast */}
          <tr className="border-b border-amber-100">
            <td className="py-3 px-3 font-medium text-gray-900">Breakfast</td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-36">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={localValues.breakfast_cost_per_attendee}
                    onChange={(e) => handleChange('breakfast_cost_per_attendee', e.target.value)}
                    onFocus={() => handleFocus('breakfast_cost_per_attendee')}
                    onBlur={() => handleBlur('breakfast_cost_per_attendee')}
                    className="text-right"
                    leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={localValues.breakfast_count}
                    onChange={(e) => handleChange('breakfast_count', e.target.value)}
                    className="text-right"
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(breakfastTotal)}</span>
            </td>
          </tr>

          {/* Lunch */}
          <tr className="border-b border-amber-100">
            <td className="py-3 px-3 font-medium text-gray-900">Lunch</td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-36">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={localValues.lunch_cost_per_attendee}
                    onChange={(e) => handleChange('lunch_cost_per_attendee', e.target.value)}
                    onFocus={() => handleFocus('lunch_cost_per_attendee')}
                    onBlur={() => handleBlur('lunch_cost_per_attendee')}
                    className="text-right"
                    leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={localValues.lunch_count}
                    onChange={(e) => handleChange('lunch_count', e.target.value)}
                    className="text-right"
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(lunchTotal)}</span>
            </td>
          </tr>

          {/* Dinner */}
          <tr className="border-b border-amber-100">
            <td className="py-3 px-3 font-medium text-gray-900">Dinner</td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-36">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={localValues.dinner_cost_per_attendee}
                    onChange={(e) => handleChange('dinner_cost_per_attendee', e.target.value)}
                    onFocus={() => handleFocus('dinner_cost_per_attendee')}
                    onBlur={() => handleBlur('dinner_cost_per_attendee')}
                    className="text-right"
                    leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={localValues.dinner_count}
                    onChange={(e) => handleChange('dinner_count', e.target.value)}
                    className="text-right"
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(dinnerTotal)}</span>
            </td>
          </tr>

          {/* Alcohol */}
          <tr className="border-b border-amber-100">
            <td className="py-3 px-3 font-medium text-gray-900">Alcohol</td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-36">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={localValues.alcohol_cost_per_attendee}
                    onChange={(e) => handleChange('alcohol_cost_per_attendee', e.target.value)}
                    onFocus={() => handleFocus('alcohol_cost_per_attendee')}
                    onBlur={() => handleBlur('alcohol_cost_per_attendee')}
                    className="text-right"
                    leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={localValues.alcohol_count}
                    onChange={(e) => handleChange('alcohol_count', e.target.value)}
                    className="text-right"
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(alcoholTotal)}</span>
            </td>
          </tr>

          {/* Total Row */}
          <tr className="bg-amber-100">
            <td className="py-3 px-3 font-bold text-amber-900" colSpan={3}>
              Total Meals
            </td>
            <td className="py-3 px-3 text-right font-bold text-amber-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(grandTotal)}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

